import Cache from '@fs/zion-cache'
import { mutate } from 'swr'
import axios from '@fs/zion-axios'
import type {
  RelationshipId,
  PersonId,
  ParentChildRelationship,
  CoupleRelationship,
  RelationshipType,
  CoupleId,
} from '@fs/zion-tree-types'
import type { Relationship } from '../person-page/components/family-members/relationship-dialog/contexts/RelationshipContext'
import type { SwrResponse } from './useSwrRequest'
import { useSwrRequest, updateRequestMultiple } from './useSwrRequest'
import { swrKey as swrKeyFamilyMembers } from './useFocusFamilyMembers'
import { swrKey as swrKeyChangeLogSummary } from './useChangeLogSummary'
import { mutateChildren } from './useChildren'

const coupleRelationshipsCache = new Cache({
  storeName: 'couple_relationships_r9',
  encrypted: process.env.NODE_ENV === 'production',
})
const parentChildRelationshipsCache = new Cache({
  storeName: 'parent_child_relationships_r9',
  encrypted: process.env.NODE_ENV === 'production',
})
export const swrKey = (relationshipType: RelationshipType, relationshipId: RelationshipId | undefined): string => {
  const isNotAssociation = relationshipType !== 'association'
  return `/service/tree/tree-data/${isNotAssociation ? 'v8/' : ''}relationship/${relationshipType}/${relationshipId}${
    isNotAssociation ? '?includePhotos=true' : ''
  }`
}

export type RelationshipDataResponse<T extends Relationship> = SwrResponse<T> & { relationship?: T }

/**
 * Get a couple relationship's information. This does not return the couple itself, just the relationship information.
 */
export function useRelationshipData<T extends Relationship>(
  relationshipType: RelationshipType | undefined,
  relationshipId: RelationshipId | undefined
): RelationshipDataResponse<T> {
  const url =
    relationshipId && relationshipType && relationshipType !== 'association'
      ? swrKey(relationshipType, relationshipId)
      : null
  const cache = relationshipType === 'couple' ? coupleRelationshipsCache : parentChildRelationshipsCache
  const response = useSwrRequest<T>(url, cache)
  return {
    ...response,
    relationship: response.data,
  }
}

export function useParentChildRelationship(
  relationshipId: RelationshipId
): RelationshipDataResponse<ParentChildRelationship> {
  // eslint-disable-next-line sonarjs/no-duplicate-string -- Tis fine
  return useRelationshipData('parent-child', relationshipId)
}
export function useCoupleRelationship(relationshipId: RelationshipId): RelationshipDataResponse<CoupleRelationship> {
  return useRelationshipData('couple', relationshipId)
}

export function mutateRelationshipData(relationshipId: RelationshipId, relationshipType: RelationshipType): void {
  mutate(swrKey(relationshipType, relationshipId))
}
function getRemoveSWRKeys(
  relationshipType: RelationshipType,
  relationshipId: RelationshipId,
  focusPersonId: PersonId
): string[] {
  return [
    swrKey(relationshipType, relationshipId),
    swrKeyFamilyMembers(focusPersonId),
    swrKeyChangeLogSummary(focusPersonId),
  ]
}
type DeleteRelationshipProps = {
  relationshipId: RelationshipId
  relationshipType: 'parent-child' | 'couple'
  reason: string
  focusPersonId: PersonId // only used for mutate functions
  coupleId: CoupleId // Only used for mutate children
}
// for parent-child relationships it will delete/remove both parents. For couple relationships it will delete the couple relationship.
export async function deleteRelationship({
  relationshipId,
  relationshipType,
  reason,
  focusPersonId,
  coupleId,
}: DeleteRelationshipProps): Promise<void> {
  const url = `/service/tree/tree-data/relationship/${relationshipType}/${relationshipId}/delete`
  await updateRequestMultiple(getRemoveSWRKeys(relationshipType, relationshipId, focusPersonId), () =>
    axios.put(url, { reason })
  )
  if (relationshipType === 'parent-child' && coupleId) {
    await mutateChildren(coupleId, focusPersonId)
  }
}

export async function removeSpouse(
  relationshipId: RelationshipId,
  // Only the surviving parent/spouse id is passed
  parent1Id: PersonId | undefined,
  parent2Id: PersonId | undefined,
  justification: string,
  focusPersonId: PersonId
): Promise<void> {
  const url = `/service/tree/tree-data/relationship/couple/${relationshipId}/family`
  /* addingParent1 will be true when removing parent1 because we want the text 'add spouse'
   * why we need to do this at all and only for parent1 I have know idea, this is just how the API works
   */

  const payload = { addingParent1: parent1Id === undefined, justification, parent1Id, parent2Id }
  // update swrKey to handle multiple keys/urls
  await updateRequestMultiple(getRemoveSWRKeys('couple', relationshipId, focusPersonId), () =>
    axios.put(url, payload)
  )
}

// removing just parent1 or parent2 from parent-child relationship.
// if it is a single-parent-child relationship, this will delete the relationship.
export async function removeParent(
  relationshipId: RelationshipId,
  justification: string,
  focusPersonId: PersonId,
  parentPosition: 'parent1' | 'parent2'
): Promise<void> {
  const url = `/service/tree/tree-data/relationship/parent-child/${relationshipId}/${parentPosition}`
  const payload = { justification }

  await updateRequestMultiple(getRemoveSWRKeys('parent-child', relationshipId, focusPersonId), () =>
    axios.put(url, payload)
  )
}

export async function updateRelationshipReason(
  // The person Id that the reason statement belongs to.
  personId: PersonId,
  relationshipId: RelationshipId,
  roleType: 'spouse1' | 'spouse2' | 'child',
  justification: string,
  // focusPersonId is only used for the mutate function
  focusPersonId: PersonId
): Promise<void> {
  const url = `/service/tree/tree-data/v8/person/${personId}/relationship/${relationshipId}`
  // Role must be all uppercase or API wont accept it
  const payload = { role: roleType.toUpperCase(), newPersonId: personId, changeMessage: justification }
  const relationshipType = roleType === 'child' ? 'parent-child' : 'couple'
  await updateRequestMultiple([swrKey(relationshipType, relationshipId), swrKeyChangeLogSummary(focusPersonId)], () =>
    axios.put(url, payload)
  )
}
